<template>
  <div
    class="mission-card-hexagon"
    :class="[
      `is-${achievementStatus}`,
      `color-type-${mission.colorType}`,
      `condition-type-${mission.honeycombType}`,
      { 'is-all-completed': mission.hasCompleted },
    ]"
  >
    <div class="mission-card-hexagon__outer">
      <mission-hexagon-outer-icon />
    </div>
    <div class="mission-card-hexagon__inner" v-if="achievementStatus === 'completed'">
      <mission-hexagon-inner-icon />
    </div>
    <p class="mission-card-hexagon__name">
      {{ circuit.circuitName }}
    </p>
    <p class="mission-card-hexagon__image">
      <mission-motegi-icon
        v-if="circuit.circuitName === CHECKED_IN_MOBILITY_RESORT_MOTEGI.circuitName"
      />
      <mission-fuji-icon v-if="circuit.circuitName === CHECKED_IN_FUJI_SPEEDWAY.circuitName" />
      <mission-suzuka-icon v-if="circuit.circuitName === CHECKED_IN_SUZUKA_CIRCUIT.circuitName" />
      <mission-autopolis-icon v-if="circuit.circuitName === CHECKED_IN_AUTOPOLIS.circuitName" />
      <mission-sugo-icon v-if="circuit.circuitName === CHECKED_IN_SPORTLAND_SUGO.circuitName" />
    </p>
    <p class="mission-card-hexagon__point" v-if="achievementStatus !== 'completed'">
      +{{ condition.point }}P
    </p>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, Ref } from '@vue/composition-api'
import DeviceInfo from '@/util/DeviceInfo'
import MissionHexagonOuterIcon from '@/components/MissionPage/MissionListPane/parts/icons/MissionHexagonOuterIcon.vue'
import MissionHexagonInnerIcon from '@/components/MissionPage/MissionListPane/parts/icons/MissionHexagonInnerIcon.vue'
import { MissionProgressCardType } from '@/components/MissionPage/hook/useMissionProgressData'
import { AchievementConditionType } from '@/store/stores/collectionModule/documents/mission/MissionProgressDocument'
import MissionMotegiIcon from '@/components/MissionPage/MissionListPane/parts/icons/circuits/MissionMotegiIcon.vue'
import MissionFujiIcon from '@/components/MissionPage/MissionListPane/parts/icons/circuits/MissionFujiIcon.vue'
import MissionSuzukaIcon from '@/components/MissionPage/MissionListPane/parts/icons/circuits/MissionSuzukaIcon.vue'
import MissionAutopolisIcon from '@/components/MissionPage/MissionListPane/parts/icons/circuits/MissionAutopolisIcon.vue'
import MissionSugoIcon from '@/components/MissionPage/MissionListPane/parts/icons/circuits/MissionSugoIcon.vue'
import {
  CHECKED_IN_CIRCUIT_LIST,
  CHECKED_IN_SUZUKA_CIRCUIT,
  CHECKED_IN_AUTOPOLIS,
  CHECKED_IN_SPORTLAND_SUGO,
  CHECKED_IN_FUJI_SPEEDWAY,
  CHECKED_IN_MOBILITY_RESORT_MOTEGI,
} from '@/util/Const'

/**
 * 1度もミッションを達成したことがない場合に前回の達成条件にセットされる値の型
 */
type NoPreviousConditionType = 'noPreviousCondition'

/**
 * ミッション達成状況を表現する型
 */
type AchievementStatusType = 'inactive' | 'active' | 'completed'

/**
 * ミッション画面 ミッション達成状況 ハニカムサーキット表示六角形パーツ
 */
export default defineComponent({
  name: 'MissionHexagonCircuitParts',
  components: {
    MissionSugoIcon,
    MissionAutopolisIcon,
    MissionSuzukaIcon,
    MissionFujiIcon,
    MissionMotegiIcon,
    MissionHexagonInnerIcon,
    MissionHexagonOuterIcon,
  },
  props: {
    /**
     * 達成条件
     */
    condition: {
      type: Object as PropType<AchievementConditionType>,
      required: true,
    },
    /**
     * 前回の達成条件
     */
    prevCondition: {
      type: [Object, String] as PropType<AchievementConditionType | NoPreviousConditionType>,
    },
    /**
     * ミッション達成状況カード
     */
    mission: {
      type: Object as PropType<MissionProgressCardType>,
      required: true,
    },
  },
  setup(props) {
    const forPc = DeviceInfo.isForPC()

    const iconSize = computed(() => {
      if (props.mission.hasCompleted && forPc) {
        return { x: 74, y: 74 }
      }

      if (props.mission.hasCompleted) {
        return { x: 54, y: 54 }
      }

      if (forPc) {
        return { x: 33, y: 33 }
      }

      return { x: 24, y: 24 }
    })

    /**
     * active状態の取得する
     */
    const getActiveStatus = () => {
      if (props.mission.honeycombType === 'check') {
        // ハニカム内がチェックマークの場合は、activeにする
        return true
      }

      if (props.prevCondition === 'noPreviousCondition') {
        // 該当ミッションを1度も達成していない場合は、最初のミッションをactiveにする
        return true
      }
      // 該当ミッションの一つ前の回数を達成している場合はactiveにする
      return props.prevCondition?.isAchieved
    }

    /**
     * ミッション達成状況のステータスを返す
     */
    const achievementStatus: Ref<AchievementStatusType> = computed(() => {
      if (props.condition.isAchieved) {
        // ハニカム内の該当回数達成済みの場合
        return 'completed'
      }
      if (getActiveStatus()) {
        return 'active'
      }

      // 上記以外
      return 'inactive'
    })

    /**
     * 対象のサーキット情報を取得する
     */
    const circuit = CHECKED_IN_CIRCUIT_LIST.find(
      (checkInCircuit) => props.condition.pointCode?.indexOf(checkInCircuit.missionCode) !== -1,
    )

    return {
      iconSize,
      achievementStatus,
      circuit,
      CHECKED_IN_SUZUKA_CIRCUIT,
      CHECKED_IN_AUTOPOLIS,
      CHECKED_IN_SPORTLAND_SUGO,
      CHECKED_IN_FUJI_SPEEDWAY,
      CHECKED_IN_MOBILITY_RESORT_MOTEGI,
    }
  },
})
</script>

<style scoped lang="scss">
.mission-card-hexagon {
  position: relative;
  display: flex;
  gap: 2px;
  flex-shrink: 0;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: min(#{convertPXtoVW(84)}, 84px);
  height: min(#{convertPXtoVW(96)}, 96px);

  &.color-type-1 {
    color: #73ffdd;
  }

  &.color-type-2 {
    color: #e0ffa0;
  }

  &.color-type-3 {
    color: #f7c6ff;
  }

  &.color-type-4 {
    color: #ff5d5d;
  }

  &__outer {
    position: absolute;
    top: 50%;
    left: 50%;
    width: min(#{convertPXtoVW(84)}, 84px);
    height: min(#{convertPXtoVW(96)}, 96px);
    opacity: 0.2;
    transform: translate(-50%, -50%);
  }

  &__inner {
    position: absolute;
    top: 50%;
    left: 50%;
    width: min(#{convertPXtoVW(74)}, 74px);
    height: min(#{convertPXtoVW(83)}, 83px);
    opacity: 0.4;
    transform: translate(-50%, -50%);
  }

  &__name {
    font-family: $fontTelemetrySpeedGear;
    font-size: min(#{convertPXtoVW(10)}, 10px);
    font-weight: 500;
    letter-spacing: 0.02em;
  }

  &__image {
    width: 37px;
    height: 27px;
  }

  &__point {
    font-size: min(#{convertPXtoVW(10)}, 10px);
    font-weight: 500;
  }

  &.is-inactive {
    .mission-card-hexagon__name,
    .mission-card-hexagon__image,
    .mission-card-hexagon__point {
      opacity: 0.3;
    }
  }

  &.is-completed {
    .mission-card-hexagon__outer {
      opacity: 0.6;
    }
  }

  &.is-all-completed {
    width: 62px;
    height: 71px;
    opacity: 0.8;

    &.color-type-1 {
      color: #b8f9e9;
    }

    .mission-card-hexagon__name {
      font-size: 10px;
    }

    .mission-card-hexagon__image {
      width: 26px;
    }

    .mission-card-hexagon__outer {
      width: 62px;
      height: 71px;
    }

    .mission-card-hexagon__inner {
      width: 55px;
      height: 62px;
    }
  }

  @media screen and (min-width: $responsiveTabletWidth) {
    width: 115px;
    height: 132px;

    &__outer {
      width: 115px;
      height: 132px;
    }

    &__inner {
      width: 101px;
      height: 113px;
    }

    &__name {
      font-size: 13px;
    }

    &__image {
      width: 36px;
    }

    &__point {
      font-size: 10px;
    }

    &.is-all-completed {
      width: 115px;
      height: 132px;

      .mission-card-hexagon__outer {
        width: 115px;
        height: 132px;
      }

      .mission-card-hexagon__inner {
        width: 101px;
        height: 113px;
      }

      .mission-card-hexagon__name {
        font-size: 13px;
      }

      .mission-card-hexagon__image {
        width: 36px;
      }

      .mission-card-hexagon__point {
        font-size: 10px;
      }
    }
  }
}
</style>
